import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-date-picker";
import { Col, Row } from "reactstrap";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useSocialPostCore } from "../../../cores/socialPost";
import {
  SocialPostStatusOptions,
  SocialStoryStatusOptions,
  SocialTargetOptions,
  SocialProviderNameMapper,
  getSocialProviderDisplayName,
  isInstagramSocialConnected,
  resolveInstagramSocialSetting,
} from "../../../cores/socialPost/config";
import { SocialProvider } from "../../../cores/socialPost/interfaces/socialProvider";
import { useIntl } from "react-intl";
import { InputWembii } from "../InputWembii";
import { LabelWembii } from "../LabelWembii";
import { ableToPost } from "../socialPost/selector/SocialPostSelector";
import { ableToStory } from "../socialPost/selector/SocialStorySelector";
import "./SocialHistoryBar.scss";

export const SocialHistoryBar = () => {
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const { company, socialSettings } = useMeCore();
  const { getSocialPosts } = useSocialPostCore();
  const [since, setSince] = useState(
    moment().startOf("month").toDate()
  );
  const [until, setUntil] = useState(
    moment().add(1, "month").endOf("month").toDate()
  );
  const [provider, setProvider] = useState("all");
  const [status, setStatus] = useState("");
  const [target, setTarget] = useState("post");

  const socialProviders: SocialProvider[] = useMemo(
    () =>
      (target === "post" ? ableToPost : ableToStory)
        .filter((social) =>
          social === "instagram"
            ? isInstagramSocialConnected(
                socialSettings as Record<string, unknown>
              )
            : Boolean(socialSettings![social])
        )
        .map((social): SocialProvider => {
          const igSetting =
            social === "instagram"
              ? resolveInstagramSocialSetting(
                  socialSettings as Record<string, unknown>
                )
              : (socialSettings![social] as Record<string, unknown> | undefined);
          return {
            id: social,
            name:
              getSocialProviderDisplayName(social) ||
              SocialProviderNameMapper[social] ||
              social,
            displayName: igSetting?.name as string | undefined,
            avatar: igSetting?.avatarUrl as string | undefined,
          };
        }),
    [socialSettings, target]
  );

  const socialStatus = useMemo(() => 
    target === "post" ? SocialPostStatusOptions : SocialStoryStatusOptions
  , [target]);

  const onChangeTarget = useCallback((target) => {
    setTarget(target);
    setProvider("all");
    setStatus("");
  }, []);

  useEffect(() => {
    if (since && until) {
      getSocialPosts(
        company?.id!,
        moment(since).format("YYYY-MM-DD"),
        moment(until).format("YYYY-MM-DD"),
        provider,
        status,
        target
      );
    }
  }, [company, until, since, provider, status, target, getSocialPosts]);

  return (
    <div
      className={cn("social-history-bar w-100", {
        "social-history-bar--dark": isDarkMode,
      })}
    >
      <div className="social-history-bar__glow" aria-hidden />
      <div className="social-history-bar__inner">
        <div className="social-history-bar__head">
          <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"]} />
          <p className="social-history-bar__headTitle">
            {intl.formatMessage({ id: "app.socialHistory.filtersTitle" })}
          </p>
          <p className="social-history-bar__headHint">
            {intl.formatMessage({ id: "app.socialHistory.filtersHint" })}
          </p>
        </div>
        <Row className="align-items-end">
        <Col xs="12" lg="2" md="6" sm="12" className="d-flex social-history-bar__field">
          <InputWembii
            label={intl.formatMessage({ id: "app.socialManager.socialTxt" })}
            type="select"
            mb={false}
            value={provider}
            onChange={(v) => setProvider(v)}
            className="w-100"
          >
            <option key="all" value="all">
             {intl.formatMessage({ id: "app.socialManager.allTxt" })} 
            </option>
            {socialProviders.map((provider) => (
              <option
                key={provider.id}
                value={provider.id}
                className="text-capitalize"
              >
                {provider.name}
              </option>
            ))}
          </InputWembii>
        </Col>
        <Col xs="12" lg="2" md="6" sm="12" className="d-flex social-history-bar__field">
          <InputWembii
            label={intl.formatMessage({ id: "app.socialManager.stateTxt" })} 
            type="select"
            mb={false}
            value={status}
            onChange={(v) => setStatus(v)}
            className="w-100"
          >
            <option key="all" value="">
             {intl.formatMessage({ id: "app.socialManager.allTxt" })} 
            </option>
            {socialStatus.map(({ value, label }) => (
              <option key={value} value={value} className="text-capitalize">
                {label}
              </option>
            ))}
          </InputWembii>
        </Col>
        <Col xs="12" lg="2" md="6" sm="12" className="d-flex social-history-bar__field">
          <InputWembii
            label={intl.formatMessage({ id: "app.socialManager.typeTxt" })}
            type="select"
            mb={false}
            value={target}
            onChange={(v) => onChangeTarget(v)}
            className="w-100"
          >
            {SocialTargetOptions.map(({ value, label }) => (
              <option key={value} value={value} className="text-capitalize">
                {label}
              </option>
            ))}
          </InputWembii>
        </Col>
        <Col
          xs="12"
          lg="6"
          md="12"
          sm="12"
          className="d-flex justify-content-lg-end social-history-bar__dates mt-3 mt-lg-0"
        >
          <div className="d-flex flex-column social-history-bar__date mr-2 mr-md-3">
            <LabelWembii>
              {intl.formatMessage({ id: "app.socialManager.from" })}
            </LabelWembii>
            <DatePicker
              onChange={setSince}
              value={since}
              clearIcon={null}
              format="dd/MM/y"
            />
          </div>
          <div className="d-flex flex-column social-history-bar__date">
            <LabelWembii>
              {intl.formatMessage({ id: "app.socialManager.to" })}
            </LabelWembii>
            <DatePicker
              onChange={setUntil}
              value={until}
              clearIcon={null}
              format="dd/MM/y"
            />
          </div>
        </Col>
      </Row>
      </div>
    </div>
  );
};
